import { Comedian, ComedianLite } from "@/data/comedians";
import {
  SentryFeatureTags,
  logException,
  logValidationError,
} from "@/analytics/sentry";
import { UserEvents, VenueEvents, trackEvent } from "@/analytics";

import { Database } from "@/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { VenuePagesRow } from "./venue_pages";
import dayjs from "@punchup/utils/dayjs";
import { getUser } from "./supabase";

export async function getUserFollowsComedian(
  supabase: SupabaseClient<Database>,
  comedian: Comedian | { id: string }
): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return false;
  }
  const { status, data, error } = await supabase
    .from("user_comedian_follows")
    .select("user_id")
    .eq("user_id", user.id)
    .eq("comedian_id", comedian.id)
    .maybeSingle();

  if (error) {
    logException(error, {
      extra: {
        data: "[getUserFollowsComedian] Error finding follow of user",
        comedian_id: comedian.id,
        user: user.id,
      },
      tags: {
        supabase: true,
      },
    });
  } else if (status === 200) {
    if (data) {
      return true;
    } else {
      return false;
    }
  }

  logValidationError("[getUserFollowsComedian] Error finding follow of user", {
    extra: {
      status,
      comedian_id: comedian.id,
      user: user.id,
    },
    tags: {
      supabase: true,
    },
  });
  throw new Error(`Error finding follow of ${comedian.id} for user ${user.id}`);
}

export async function getFollowedComedians({
  supabase,
  callerIsServer = false,
}: {
  supabase: SupabaseClient<Database>;
  callerIsServer?: boolean;
}): Promise<{ comedian_id: string }[]> {
  let userId = "";
  if (callerIsServer) {
    const user = await getUser(supabase);
    if (!user) {
      throw new Error(
        "Supabase user must be logged in to get followed comedians."
      );
    }
    userId = user.id;
  } else {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to get followed comedians."
      );
    }
    userId = session.user.id;
  }

  const { error, data } = await supabase
    .from("user_comedian_follows")
    .select("comedian_id")
    .eq("user_id", userId);

  if (error) {
    logException(error, {
      extra: {
        data: `[getFollowedComedians] Error getting followed comedians for user ${userId}`,
        userId,
      },
      tags: {
        supabase: true,
        userId,
      },
    });
    return [];
  }

  return data;
}

export async function getFollowedComediansAndTheirDetails(
  supabase: SupabaseClient<Database>
): Promise<ComedianLite[]> {
  const filter: {
    user_id?: string;
  } = {};
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user) {
    filter["user_id"] = user.id;
  }
  const { status, data, error } = await supabase
    .from("user_comedian_follows")
    .select(
      `
      comedian:comedians!inner (
        id,
        display_name,
        display_name_short,
        slug,
        about_img,
        published_at
      )
    `
    )
    .match(filter)
    .lte("comedian.published_at", dayjs().toISOString());

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      // Flatten the array and remove any null or undefined values
      const flattenedComedians = data
        .flatMap((item) => (item.comedian ? [item.comedian] : []))
        .filter(Boolean);
      return flattenedComedians;
    }
  }
  throw new Error(
    `Error getting user comedina follows and their comedians for a user`
  );
}

export async function followComediansById(
  supabase: SupabaseClient<Database>,
  comedianIds: Comedian["id"][],
  followLocation: FOLLOW_LOCATIONS
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to follow comedian."
    );
  }

  // fetch the existing follows for the user, so we can later determine which ones are new
  const { data: existingFollows, error: fetchError } = await supabase
    .from("user_comedian_follows")
    .select("comedian_id")
    .eq("user_id", session.user.id);

  if (fetchError) {
    console.error(fetchError);
    throw new Error(
      `Error fetching existing follows for user ${session.user.id}: ${fetchError.message}`
    );
  }

  // upsert will insert if a row doesn't exist.
  // this is needed to deal with signin via the
  // comedian checkout flow, when a user already follows.
  const {
    data: upsertData,
    status,
    error,
  } = await supabase
    .from("user_comedian_follows")
    .upsert(
      [
        ...comedianIds.map((comedianId) => ({
          user_id: session.user.id,
          comedian_id: comedianId,
        })),
      ],
      {
        onConflict: "user_id,comedian_id",
        ignoreDuplicates: true,
        count: "exact",
      }
    )
    .select();

  if (error) {
    console.error(error);
  } else if (status === 201) {
    const followingComedianIds = existingFollows.map(
      (comedian) => comedian.comedian_id
    );

    // iterate over only the new follows to track an analyics event for each
    upsertData
      .filter(
        (upsertResponse) =>
          !followingComedianIds.includes(upsertResponse.comedian_id)
      )
      .map((upsertResponse) =>
        trackEvent(UserEvents.FOLLOWED_COMEDIAN, {
          ComedianId: upsertResponse.comedian_id,
          followLocation,
        })
      );

    return true;
  } else {
    return false;
  }

  throw new Error(
    `Error attemping for user ${session.user.id} following comedians: ${comedianIds}`
  );
}

export enum FOLLOW_LOCATIONS {
  OTP_SIGNIN = "otp-signin",
  OAUTH_SIGNIN = "oauth-signin",
  FREE_CHECKOUT_ALREADY_LOGGED_IN = "free-checkout-already-logged-in",
  PAID_CHECKOUT_ALREADY_LOGGED_IN = "paid-checkout-already-logged-in",
  PAYMENT_FORM = "payment-form",
  SUGGESTED = "suggested",
  LIBRARY = "library",
  COMEDIAN_LANDING_PAGE = "comedian-landing-page",
  COMEDIAN_FOLLOW_PAGE = "comedian-follow-page",
  FEED_FEATURED_COMEDIAN_CARD = "feed-featured-comedian-card",
  FEED_SUGGESTED_COMEDIAN_CARD = "feed-suggested-comedian-card",
  FEED_VIDEO_CARD = "feed-video-card",
  SEARCH = "search",
  EVENT_LANDING = "event-landing",
  VENUE_PAGE = "venue-page",
  CLAP_REACTION_BUTTON = "clap-reaction-button",
  SHOWS_NEARBY = "shows-nearby",
}

export async function followComedian(
  supabase: SupabaseClient<Database>,
  comedian: Pick<Comedian, "id">,
  followLocation: FOLLOW_LOCATIONS
) {
  // pass only a single comedian id
  return followComediansById(supabase, [comedian.id], followLocation);
}

export async function unFollowComedian(
  supabase: SupabaseClient<Database>,
  comedian: Pick<Comedian, "id">,
  unfollowLocation?: string
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to unfollow comedian."
    );
  }

  const { count, status, error } = await supabase
    .from("user_comedian_follows")
    .delete({ count: "exact" })
    .match({ user_id: session.user.id, comedian_id: comedian.id });

  if (error) {
    console.error(error);
    return { error: error };
  } else if (status === 204 && count === 1) {
    trackEvent(UserEvents.UNFOLLOWED_COMEDIAN, {
      ComedianId: comedian.id,
      unfollowLocation,
    });
    return true;
  } else {
    const error = new Error(
      `Error unfollowing comedian ${comedian.id} for user with id ${session.user.id}.`
    );
    return { error: error };
  }
}

// Follow a single venue page
export async function followVenue(
  supabase: SupabaseClient<Database>,
  venue: Pick<VenuePagesRow, "id" | "slug">,
  followLocation: FOLLOW_LOCATIONS
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to follow a venue."
    );
  }
  try {
    await supabase
      .from("user_venue_follows")
      .insert({ user_id: session.user.id, venue_page_id: venue.id })
      .throwOnError();
    trackEvent(VenueEvents.FOLLOWED_VENUE, {
      VenueId: venue.id,
      venueSlug: venue.slug,
      followLocation,
    });
  } catch (err) {
    logException(err, {
      data: `Error while attempting to follow venue page with id ${venue.id}, by user with id ${session.user.id}`,
    });
  }
}

// Unfollow a single venue page
export async function unfollowVenue(
  supabase: SupabaseClient<Database>,
  venue: Pick<VenuePagesRow, "id" | "slug">,
  unfollowLocation: FOLLOW_LOCATIONS
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to unfollow a venue."
    );
  }
  try {
    await supabase
      .from("user_venue_follows")
      .delete()
      .match({ user_id: session.user.id, venue_page_id: venue.id })
      .throwOnError();

    trackEvent(VenueEvents.UNFOLLOWED_VENUE, {
      VenueId: venue.id,
      venueSlug: venue.slug,
      unfollowLocation,
    });
  } catch (err) {
    logException(err, {
      data: `Error while attempting to unfollow venue page with id ${venue.id}, by user with id ${session.user.id}`,
    });
  }
}
